import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, provideAppInitializer, inject } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatPaginatorIntl } from '@angular/material/paginator';

// icons
import { TablerIconsModule } from 'angular-tabler-icons';
import * as TablerIcons from 'angular-tabler-icons/icons';

// perfect scrollbar
import { NgScrollbarModule } from 'ngx-scrollbar';

//Import all material modules
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAuth, LogLevel, AbstractSecurityStorage, authInterceptor } from 'angular-auth-oidc-client';
//import { Gateway_BASE_URL } from './services/sales.api.client';
import { Gateway_BASE_URL } from 'src/app/services/settings.api.client';
import { LocalStorageService } from 'src/app/services/local-storage-service';
import { MaterialModule } from 'src/app/material.module';
import { environment } from './environments/environment.dev';
import { patchDateToJson } from 'src/app/services/date-utils';
import { CustomPaginatorIntl } from 'src/app/services/custom-paginator-intl';
import { Wiki_BASE_URL, Wiki_ROUTE_PREFIX } from 'src/app/services/wiki-help.service';
import { KbChat_GATEWAY_BASE_URL, KbChat_LOCAL_BASE_URL } from 'src/app/services/kb-chat.service';
import { ZohoIm_CHANNEL } from 'src/app/services/zoho-im.service';
import { ClarityService, Clarity_PROJECT_ID } from 'src/app/services/clarity.service';

export function HttpLoaderFactory(http: HttpClient): any {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Patch date serialization globally before the app starts
// override how Date is serialized once globally with a custom toJSON() that uses local time:
patchDateToJson();

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding()
    ),
    provideHttpClient(withInterceptors([authInterceptor()])),
    provideClientHydration(),
    provideAnimationsAsync(),
    importProvidersFrom(
      FormsModule,
      ReactiveFormsModule,
      MaterialModule,
      TablerIconsModule.pick(TablerIcons),
      NgScrollbarModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideAuth({
      config: {
        //postLoginRoute: '/home',
        postLoginRoute: '/',
        forbiddenRoute: '/forbidden',
        unauthorizedRoute: '/unauthorized',
        //historyCleanupOff: true,
        authority: environment.authServerBaseURL,
        redirectUrl: window.location.origin + environment.base_href,
        postLogoutRedirectUri: window.location.origin + environment.base_href,
        clientId: environment.authSettings.clientId,
        scope: 'openid profile email offline_access wrapper_apis',
        responseType: 'code',
        silentRenew: true,
        silentRenewUrl: `${window.location.origin + environment.base_href}/silent-renew.html`,
        useRefreshToken: true,
        renewTimeBeforeTokenExpiresInSeconds: 30,
        // logLevel: LogLevel.Debug,
        ignoreNonceAfterRefresh: true,
        autoUserInfo: true,
        renewUserInfoAfterTokenRenew: true,
        secureRoutes: [environment.apiGatewayBaseURL],
      },
    }),
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
    { provide: AbstractSecurityStorage, useClass: LocalStorageService },
    {
      provide: Gateway_BASE_URL,
      useFactory: () => {
        return environment.apiGatewayBaseURL;
      },
    },
    // Ask help panel: related wiki articles for this app's routes.
    {
      provide: Wiki_BASE_URL,
      useFactory: () => {
        return environment.wikiApiBaseURL;
      },
    },
    {
      provide: Wiki_ROUTE_PREFIX,
      useFactory: () => {
        return environment.base_href;
      },
    },
    // Ask AI chat: gateway proxy (CHAT-13) and the local-dev override, both @Optional() in
    // KbChatService — an app that leaves chatLocalBaseURL empty just uses the gateway.
    {
      provide: KbChat_GATEWAY_BASE_URL,
      useFactory: () => {
        return environment.apiGatewayBaseURL;
      },
    },
    {
      provide: KbChat_LOCAL_BASE_URL,
      useFactory: () => {
        return environment.chatLocalBaseURL;
      },
    },
    // Zoho Desk Business Messaging support chat (Ask panel "Technical support" tab).
    {
      provide: ZohoIm_CHANNEL,
      useFactory: () => {
        return environment.zohoImChannel;
      },
    },
    // Microsoft Clarity session recording, production builds only (empty ID elsewhere = no-op).
    {
      provide: Clarity_PROJECT_ID,
      useFactory: () => {
        return environment.clarityProjectId;
      },
    },
    provideAppInitializer(() => inject(ClarityService).init()),
  ],
};
